<script>
import VueRecaptcha from 'vue-recaptcha'
import { validationMixin } from 'vuelidate'
import { required, sameAs, email } from 'vuelidate/lib/validators'
import Layout from '@layouts/login'
import SocialLogin from './social-login'
import consentService from '@services/consent-service'
import postAuthRedirect from '@utils/post-auth-redirect'

export default {
    name: 'SignUp',
    page() {
        const title = 'Sign Up'
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: title },
                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: title },
                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: title },
            ],
        }
    },
    components: { VueRecaptcha, Layout, SocialLogin },
    mixins: [validationMixin],
    props: {
        redirect: {
            type: [String, Boolean],
            required: false,
            default: false,
        },
    },
    validations: {
        form: {
            email: {
                required,
                email,
            },
            password: {
                required,
            },
        },
        confirmEmail: {
            required,
            sameAsEmail: sameAs(function () {
                return this.form.email
            }),
            email,
        },
        confirmPassword: {
            required,
            sameAsPassword: sameAs(function () {
                return this.form.password
            }),
        },
    },
    data() {
        return {
            form: {
                email: '',
                password: '',
            },
            confirmEmail: '',
            confirmPassword: '',
            tryingToSignUp: false,
            recaptchaKey: '',
            socialBaseUrl: `${process.env.VUE_APP_API_BASE_URL}v3/auth/oauth/`,
        }
    },
    computed: {
        siteKey() {
            return process.env.VUE_APP_RECAPTCHA_SITE_KEY || ''
        },
    },
    methods: {
        _notify(messageKey, type = 'warning', params = {}) {
            this.$store.dispatch('appearance/handleNotification', {
                message: this.$m(messageKey, params),
                type,
            })
        },
        signUp() {
            if (this.$v.$invalid) {
                this._notify('EMAIL_PASSWORD_REQUIRED')
                return
            }
            if (!this.recaptchaKey) {
                this._notify('RECAPTCHA_MISSING_ERROR')
                return
            }
            this.tryingToSignUp = true
            const form = {
                ...this.form,
                username: this.form.email,
            }
            return this.$dataLayer.api
                .signup({ data: form })
                .then(res => this._successHandler(res))
                .catch(error => this._errorHandler(error))
        },
        setRecaptcha(val) {
            this.recaptchaKey = val || ''
        },
        _successHandler(res) {
            const token = res.headers['x-auth-token']
            if (token) {
                const user = { ...res.body, token }
                this.$store.dispatch('auth/setUser', { ...user, members_id: { id: user.id } })
            }
            consentService.grantConsent('signup')
            postAuthRedirect.resolveAfterAuth('/premium')
        },
        _errorHandler(error) {
            this.tryingToSignUp = false
            if (error.response.status === 409) {
                const networkUsed = error.response.headers['x-network-used']
                const messageKey = networkUsed === 'none'
                    ? 'USER_ALREADY_EXISTS'
                    : 'USER_ALREADY_EXISTS_SOCIAL'
                const params = networkUsed === 'none' ? {} : { '{social}': networkUsed }
                this._notify(messageKey, 'warning', params)
            } else {
                this._notify('SOMETHING_WENT_WRONG', 'danger')
            }
        },
    },
}
</script>

<template>
    <Layout>
        <BaseContent>
            <BaseSection padded>
                <div :class="$style.formContainer">
                    <h4 :class="$style.title">Sign up</h4>
                    <SocialLogin :is-sign-up="true" />
                    <div :class="$style.lineSeparator">OR</div>
                    <h5 :class="$style.subtitle">Sign up with your email address:</h5>
                    <form
                        :class="$style.form"
                        autocomplete="off | unknown-autocomplete-value"
                        @submit.prevent="signUp"
                    >
                        <div :class="$style.fieldContainer">
                            <BaseInput
                                v-model.lazy="form.email"
                                :class="[$style.field, $v.form.email.$error && $style.error]"
                                placeholder="email*"
                                @blur.native="$v.form.email.$touch()"
                            />
                            <label
                                v-if="$v.form.email.$error"
                                :class="[$style.label, $style.error]"
                            >This field is required and must be an email</label>
                        </div>
                        <div :class="$style.fieldContainer">
                            <BaseInput
                                v-model="confirmEmail"
                                :class="[$style.field, $v.confirmEmail.$error && $style.error]"
                                placeholder="confirm email*"
                                @blur.native="$v.confirmEmail.$touch()"
                            />
                            <label
                                v-if="$v.confirmEmail.$error"
                                :class="[$style.label, $style.error]"
                            >This field is required and must match the email above</label>
                        </div>
                        <div :class="$style.fieldContainer">
                            <BaseInput
                                v-model="form.password"
                                :class="[$style.field, $v.form.password.$error && $style.error]"
                                type="password"
                                placeholder="password*"
                                @blur.native="$v.form.password.$touch()"
                            />
                            <label
                                v-if="$v.form.password.$error"
                                :class="[$style.label, $style.error]"
                            >This field is required</label>
                        </div>
                        <div :class="$style.fieldContainer">
                            <BaseInput
                                v-model="confirmPassword"
                                :class="[$style.field, $v.confirmPassword.$error && $style.error]"
                                type="password"
                                placeholder="confirm password*"
                                @blur.native="$v.confirmPassword.$touch()"
                            />
                            <label
                                v-if="$v.confirmPassword.$error"
                                :class="[$style.label, $style.error]"
                            >This field is required and must match password above</label>
                        </div>
                        <div :class="$style.recaptchaContainer">
                            <VueRecaptcha
                                :load-recaptcha-script="true"
                                :sitekey="siteKey"
                                theme="dark"
                                @verify="setRecaptcha"
                                @expired="setRecaptcha"
                            />
                        </div>
                        <div :class="$style.agreement">
                            By signing up you agree to our
                            <a
                                :class="$style.link"
                                href="https://frisky.fm/about/terms"
                                target="_blank"
                            >Terms and Conditions</a>
                            &
                            <a
                                :class="$style.link"
                                href="https://frisky.fm/about/privacy-policy"
                                target="_blank"
                            >Privacy Policy</a>.
                        </div>
                        <BaseButton
                            :class="$style.button"
                            :disabled="tryingToSignUp"
                            :modifiers="{ fill: true }"
                            button-type="highlight"
                            type="submit"
                        >
                            Sign Up
                        </BaseButton>
                        <div :class="$style.additionalOptions">
                            <span :class="$style.additionalText">Already have an account?</span>
                            <BaseLink :to="{ name: 'login' }">
                                <BaseButton button-type="tagBlack">Log In</BaseButton>
                            </BaseLink>
                        </div>
                    </form>
                </div>
            </BaseSection>
        </BaseContent>
    </Layout>
</template>

<style lang="scss" module>
@import '@design';

.formContainer {
    position: relative;
    margin-bottom: $s-h;
    .field {
        &.error {
            margin-bottom: $s-xl;
            border-color: $c-punch-red;
            border-width: 2px;
        }
    }
}
.fieldContainer {
    position: relative;
    width: 100%;
}
.form {
    display: flex;
    flex-direction: column;
    align-items: center;
}
.label {
    @extend %contentSubtitleM;

    display: block;
    font-weight: 300;
    text-align: left;

    &.error {
        position: absolute;
        bottom: $s-xs;
        color: $c-punch-red;
    }
}
.title {
    text-align: left;
}
.subtitle {
    @extend %contentTinyHeading;

    font-weight: 400;
    text-align: left;
}

.lineSeparator {
    @include lineSeparator(true, transparentize($c-tuna, 0.5));

    @extend %contentSubtitleS;

    font-weight: 100;
}

.recaptchaContainer {
    margin-top: $s-l;
}

.button {
    margin-top: $s-xl;
}

.agreement {
    @extend %contentSubtitleS;

    margin-top: $s-l;
}

a.link,
a.link:visited {
    color: $c-torch-red;
}

.additional {
    &Options {
        margin-top: $s-h;
    }
    &Text {
        @extend %contentSubtitleM;
    }
}

@include desktop {
    .additionalText {
        margin-right: $s-xl;
    }
}
</style>
